<script setup>
import { $api } from "@/utils/api";
import { maskCpfOrCnpj, onlyDigits } from "@/utils/masks";

definePage({
  meta: {
    requiresAuth: true,
  },
});

// Filtros (somente dígitos no CPF/CNPJ)
const filtroNome = ref("");
const filtroCpfCnpj = ref("");
const filtroFuncao = ref("");

const prestadores = ref([]);
const total = ref(0);
const loading = ref(false);
const options = ref({ page: 1, itemsPerPage: 15 });
const funcoesOptions = ref([]);

// Permissão INSERIR na tela de Funcionários (integrar com CASL quando existir)
const canInserirPrestador = ref(true);

// Exibição do CPF/CNPJ com máscara (apenas dígitos no valor interno)
const cpfCnpjDisplay = computed({
  get: () => maskCpfOrCnpj(filtroCpfCnpj.value),
  set: (val) => {
    const d = onlyDigits(val).slice(0, 14);
    filtroCpfCnpj.value = d;
  },
});

async function fetchFuncoes() {
  try {
    const list = await $api("/funcoes");
    funcoesOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    console.error(e);
    funcoesOptions.value = [];
  }
}

async function fetchPrestadores() {
  loading.value = true;
  try {
    const params = new URLSearchParams({
      page: String(options.value.page),
      per_page: String(options.value.itemsPerPage),
    });
    if (filtroNome.value.trim()) params.set("nome", filtroNome.value.trim());
    if (filtroCpfCnpj.value) params.set("cpf_cnpj", filtroCpfCnpj.value);
    if (filtroFuncao.value) params.set("funcao", filtroFuncao.value);

    const data = await $api(`/prestadores?${params}`);
    prestadores.value = data.data ?? [];
    total.value = data.total ?? 0;
  } catch (e) {
    console.error(e);
    prestadores.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
}

function onPesquisar() {
  options.value.page = 1;
  fetchPrestadores();
}

function onLimparCampos() {
  filtroNome.value = "";
  filtroCpfCnpj.value = "";
  filtroFuncao.value = "";
  options.value.page = 1;
}

function onOptionsChange(opts) {
  options.value = opts;
  fetchPrestadores();
}

function formatCpfCnpj(val) {
  if (!val) return "";
  const d = onlyDigits(val);
  return d ? maskCpfOrCnpj(d) : val;
}

/** Bloqueia teclas que não sejam número ou tecla de controle no campo CPF/CNPJ */
function onCpfCnpjKeydown(e) {
  const key = e.key;
  if (/[0-9]/.test(key)) return;
  const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (allowed.includes(key)) return;
  if (e.ctrlKey || e.metaKey) {
    if (['a', 'c', 'v', 'x'].includes(key.toLowerCase())) return;
  }
  e.preventDefault();
}

onMounted(() => {
  fetchFuncoes();
  fetchPrestadores();
});
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="text-h5 pa-4 d-flex align-center">
            <span>Prestadores</span>
            <VSpacer />
            <VBtn
              v-if="canInserirPrestador"
              color="primary"
              size="small"
              :to="{ name: 'prestadores-novo' }"
            >
              <VIcon icon="tabler-plus" start />
              Novo Colaborador
            </VBtn>
          </VCardTitle>
          <VDivider />
          <VCardText class="pa-4">
            <!-- Filtros centralizados -->
            <div class="d-flex flex-column flex-md-row align-center justify-center gap-3 gap-md-4 mb-4">
              <VTextField
                v-model="filtroNome"
                label="Nome"
                placeholder="Nome ou parte do nome"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 180px; max-width: 220px;"
                prepend-inner-icon="tabler-user"
                variant="outlined"
                @keydown.enter="onPesquisar"
              />
              <VTextField
                v-model="cpfCnpjDisplay"
                label="CPF / CNPJ"
                placeholder="Somente números"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 160px; max-width: 200px;"
                prepend-inner-icon="tabler-id"
                variant="outlined"
                maxlength="18"
                @keydown="onCpfCnpjKeydown"
                @keydown.enter="onPesquisar"
              />
              <VSelect
                v-model="filtroFuncao"
                label="Função"
                :items="funcoesOptions"
                item-title="title"
                item-value="value"
                placeholder="Função"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 140px; max-width: 180px;"
                prepend-inner-icon="tabler-briefcase"
                variant="outlined"
              />
              <div class="d-flex align-center gap-1">
                <VTooltip text="Pesquisar" location="bottom">
                  <template #activator="{ props: tProps }">
                    <VBtn
                      v-bind="tProps"
                      icon
                      color="primary"
                      variant="flat"
                      size="small"
                      @click="onPesquisar"
                    >
                      <VIcon icon="tabler-search" />
                    </VBtn>
                  </template>
                </VTooltip>
                <VTooltip text="Limpar campos" location="bottom">
                  <template #activator="{ props: tProps }">
                    <VBtn
                      v-bind="tProps"
                      icon
                      variant="outlined"
                      size="small"
                      @click="onLimparCampos"
                    >
                      <VIcon icon="tabler-filter-off" />
                    </VBtn>
                  </template>
                </VTooltip>
              </div>
            </div>

            <!-- Tabela -->
            <VDataTableServer
              v-model:options="options"
              :headers="[
                { title: 'Nome', key: 'nome_razao_social', sortable: false },
                { title: 'CPF / CNPJ', key: 'cpf_cnpj', sortable: false },
                { title: 'Função', key: 'funcao', sortable: false },
                { title: 'Ações', key: 'actions', sortable: false, width: 100 },
              ]"
              :items="prestadores"
              :items-length="total"
              :loading="loading"
              loading-text="Carregando..."
              @update:options="onOptionsChange"
            >
              <template #item.nome_razao_social="{ item }">
                {{ item.nome_completo || item.nome_razao_social || '—' }}
              </template>
              <template #item.cpf_cnpj="{ item }">
                {{ formatCpfCnpj(item.cpf_cnpj) || item.cpf_cnpj || '—' }}
              </template>
              <template #item.funcao="{ item }">
                {{ item.funcao || '—' }}
              </template>
              <template #item.actions="{ item }">
                <VTooltip text="Ver detalhes">
                  <template #activator="{ props: tooltipProps }">
                    <VBtn
                      v-bind="tooltipProps"
                      icon
                      variant="text"
                      size="small"
                      :to="{ name: 'prestadores-id', params: { id: item.id } }"
                    >
                      <VIcon icon="tabler-search" />
                    </VBtn>
                  </template>
                </VTooltip>
              </template>
            </VDataTableServer>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
